import {
  AssignmentGroupingType,
  CompletionMetric,
} from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import type { SectionAssignment } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import type { ComponentProps, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState, useId } from 'react'
import type {
  Control,
  FieldError,
  UseFormReturn,
  UseFormSetValue,
} from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { AssignmentType } from '@breakoutlearning/firebase-repository/types'
import type { ButtonKind } from 'components/design-system/BreakoutButton'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutDateTimeInput } from 'components/design-system/BreakoutDateTimeInput'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { BreakoutSelect } from 'components/design-system/BreakoutSelect'
import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { ConfigureGradingScalars } from '../../assignment/dialogs/ConfigureGradingScalars'
import { useTranslationTyped } from 'i18n/i18n'
import { InfoIcon } from 'components/icons/Info'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import classNames from 'classnames'
import { FormError } from 'components/design-system/form/FormError'
import { AdminOnlyRing } from 'components/AdminOnlyRing'
import { RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import { useDialogs } from 'hooks/dialogs'
import type {
  AssignmentFormValues,
  LtiAssignmentFormValues,
} from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import { getLtiAssignmentSchema } from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import { DateTime } from 'luxon'
import { ChevronLeft } from 'components/icons/ChevronLeft'
import { PeopleGroup } from 'components/icons/PeopleGroup'
import { TooltipWrapper } from 'pages/instructor/shared/GradingTooltips'
import { Trans } from 'react-i18next'
import { Person } from 'components/icons/Person'
import { BreakoutSwitch } from 'components/design-system/BreakoutSwitch'
import { ChevronRight } from 'components/icons/ChevronRight'
import { FormStage } from './types'
import { clickOnEnter, clickOnSpace, composeClickHandlers } from 'util/keyboard'
import { BreakoutInfoTooltip } from 'components/design-system/BreakoutPrefabTooltips'
import { PeerReviewInputs } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog/PeerReviewInputs'
import { PeerReviewsToggle } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog/GroupingInputs'
import { isPeerReviewAllowed } from '@breakoutlearning/firebase-repository/sources-of-truth/isPeerReviewAllowed'
import { LateSubmissionInputs } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog/DateInputs'
import { useCutoffIsAutoDerived } from 'pages/instructor/slide_deck/AssignmentDialog/useCutoffIsAutoDerived'
import { useRepository } from 'hooks/auth'
import { useCubitBuilder } from 'hooks/cubits'
import { CreateOrEditAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/CreateOrEditAssignmentCubit'
import { Spinner } from 'components/Spinner'
import { GroupingSizeInput } from '../AssignmentDialog/BaseAssignmentDialog/GroupingSizeInput'
import { type SlideDeckRole } from '@breakoutlearning/firebase-repository/models/SlideDeckRole'
import { reaction } from 'mobx'
import { GradeAppealsSwitch } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog/GradeAppealsSwitch'
import { setFinalGradeVisibility } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog/setFinalGradeVisibility'

export const LtiBaseAssignmentDialog = ({
  className,
  earlySecondaryButtonOverride,
  form,
  forcedStage,
  fullScreen = false,
  hasBeenStarted,
  hasQuestions,
  hasRubrics,
  hideDateFields = false,
  initialAssignmentGradingScalars,
  isDraft = false,
  ltiMode = false,
  onSubmit,
  onSubmitStage,
  releasedToStudents,
  scalarsRef,
  sectionSelectComponent,
  submitButtonText,
  submitButtonIncludeChevron = false,
  disableGroupingReason,
  gradingSumValidOverride,
  currentScalarsToCheckDirty = undefined,
  slideDeckId,
  roles,
  assignment,
  advancedGradingSlot,
  onCustomizeWeights,
  customRubricsValid = true,
  completionMetric,
  onCompletionMetricChange,
}: {
  className?: string
  earlySecondaryButtonOverride?: {
    text: string
    kind?: ButtonKind
    onClick: () => Promise<void>
  }
  onSubmitStage?: (
    stage: FormStage,
    data: Partial<AssignmentFormValues>
  ) => Promise<void>
  form: UseFormReturn<AssignmentFormValues>
  forcedStage?: FormStage
  fullScreen?: boolean
  disableGroupingReason?: string
  hasBeenStarted: boolean
  hasQuestions: boolean
  hasRubrics: boolean
  hideDateFields?: boolean
  initialAssignmentGradingScalars?: SectionAssignment['data']['assignmentGradingScalars']
  isDraft?: boolean
  ltiMode?: boolean
  onSubmit: (data: LtiAssignmentFormValues) => Promise<void>
  releasedToStudents: boolean
  scalarsRef: React.RefObject<
    SectionAssignment['data']['assignmentGradingScalars']
  >
  sectionSelectComponent?: ReactNode
  submitButtonText: string
  submitButtonIncludeChevron?: boolean
  gradingSumValidOverride?: {
    gradingSumValid: boolean
    gradingSum: number
    setGradingSumCallback: ({
      isValid,
      sumAsInt,
    }: {
      isValid: boolean
      sumAsInt: number
    }) => void
  }
  /** The current grading scalars to check for dirtiness.
   * undefined = no check
   * null = check against no scalars
   */
  currentScalarsToCheckDirty?:
    | SectionAssignment['data']['assignmentGradingScalars']
    | null
  slideDeckId: string
  roles?: SlideDeckRole[]
  assignment?: SectionAssignment
  /** Optional custom-rubric panel (score mapping), shown alongside the grading
   * scalars in a second column when grading is configured. */
  advancedGradingSlot?: ReactNode
  /** Open the weights dialog (the score-mapping panel is always shown). */
  onCustomizeWeights?: () => void
  /** When an `advancedGradingSlot` is present, whether its score mappings are
   * valid — gates the submit button. */
  customRubricsValid?: boolean
  /** How Completion is measured. Absent = legacy time in session. */
  completionMetric?: CompletionMetric
  /** When provided, the Completion metric toggle renders in the Grading stage. */
  onCompletionMetricChange?: (metric: CompletionMetric) => void
}) => {
  const [_stage, setStage] = useState<FormStage>(forcedStage ?? FormStage.Dates)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingEarlySecondary, setIsLoadingEarlySecondary] = useState(false)
  const { tt } = useTranslationTyped()
  const { popDialog } = useDialogs()
  const headingId = useId()
  const stage = useMemo(() => {
    if (ltiMode && _stage === FormStage.Dates) {
      return FormStage.Grouping
    }
    return _stage
  }, [_stage, ltiMode])

  // if the parent component is supplying override, use the parent function
  // and values for gradingSum / setGrading sum, else use local state
  const [
    { gradingSumValid: _gradingSumValid, gradingSum: _gradingSum },
    _setGradingSum,
  ] = useState<{
    gradingSumValid: boolean
    gradingSum: number
  }>({ gradingSumValid: false, gradingSum: 0 })

  const gradingSumValid =
    gradingSumValidOverride?.gradingSumValid ?? _gradingSumValid
  const gradingSum = gradingSumValidOverride?.gradingSum ?? _gradingSum

  const setGradingSumCallback = useCallback(
    ({ isValid, sumAsInt }: { isValid: boolean; sumAsInt: number }) => {
      if (gradingSumValidOverride?.setGradingSumCallback)
        return gradingSumValidOverride.setGradingSumCallback({
          isValid,
          sumAsInt,
        })

      _setGradingSum({
        gradingSum: sumAsInt,
        gradingSumValid: isValid,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gradingSumValidOverride?.setGradingSumCallback]
  )

  const [initialAssignedAt, initialDueDate] = useMemo(
    () => [form.getValues('assignedAt'), form.getValues('dueDate')],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const {
    control,
    setValue,
    getValues,
    formState: { isSubmitting, errors, isValid: formValid },
    trigger,
    watch,
  } = form

  const cutoffIsAutoDerived = useCutoffIsAutoDerived(control)

  const configureGrading = watch('configureGrading')
  // The custom-rubric panel (score mapping) widens the grading stage into two
  // columns when grading is configured.
  const wideGrading =
    stage === FormStage.Grading && !!advancedGradingSlot && !!configureGrading
  const groupingType = watch('groupingType')
  const dueDate = watch('dueDate')
  const assignedAt = watch('assignedAt')
  const allowLateSubmissions = watch('allowLateSubmissions')

  const [scalarsDirty, setScalarsDirty] = useState(
    currentScalarsToCheckDirty !== undefined
  )
  const customGroupingSelected = watch('customGroupingSelected')
  const forbidStudentGroupCreation = watch('forbidStudentGroupCreation')
  const forbidStudentGroupScheduling = watch('forbidStudentGroupScheduling')
  const forbidStudentGroupJoining = watch('forbidStudentGroupJoining')
  const forbidStudentGroupLeaving = watch('forbidStudentGroupLeaving')
  const peerReviewsEnabled = watch('peerReviewsEnabled')
  const groupingSize = watch('groupingSize')
  const showFinalGradeToParticipants = watch('showFinalGradeToParticipants')
  const gradeAppealsEnabled = watch('gradeAppealsEnabled')

  const showFinalGradeChanged =
    !assignment ||
    assignment.data.showFinalGradeToParticipants !==
      showFinalGradeToParticipants
  const gradeAppealsEnabledChanged =
    !assignment || assignment.data.gradeAppealsEnabled !== gradeAppealsEnabled

  const isPeerReviewDisabled =
    !isPeerReviewAllowed(groupingSize) && stage === FormStage.PeerReviews

  const availableStages = useMemo<FormStage[]>(() => {
    const stages: FormStage[] = []
    if (!ltiMode) {
      stages.push(FormStage.Dates)
    }
    stages.push(FormStage.Grouping)
    if (peerReviewsEnabled || stage === FormStage.PeerReviews) {
      stages.push(FormStage.PeerReviews)
    }
    stages.push(FormStage.Grading)
    return stages
  }, [ltiMode, peerReviewsEnabled, stage])

  const currentStageIndex = availableStages.indexOf(stage)

  const previousStage =
    currentStageIndex > 0 ? availableStages[currentStageIndex - 1] : null

  const nextStage =
    currentStageIndex < availableStages.length - 1
      ? availableStages[currentStageIndex + 1]
      : null

  let showBackButton = forcedStage === undefined && previousStage !== null
  if (ltiMode && stage === FormStage.Grouping) showBackButton = false

  const onGradingChange = useCallback(
    ({
      scalars,
      configureGrading,
    }: {
      scalars: SectionAssignment['data']['assignmentGradingScalars'] | null
      configureGrading: boolean
    }) => {
      if (currentScalarsToCheckDirty === undefined) return setScalarsDirty(true) // not checking

      // if we went from no scalars to scalars or vice versa, dirty
      if (configureGrading !== !!currentScalarsToCheckDirty)
        return setScalarsDirty(true)

      const equal = checkScalarsAreEqual(scalars, currentScalarsToCheckDirty)
      setScalarsDirty(!equal)
    },
    [currentScalarsToCheckDirty]
  )

  // A Completion-metric-only change (weights unchanged) must still enable Save,
  // so it isn't blocked by the scalars-dirty gate below.
  const completionMetricChanged =
    (completionMetric ?? CompletionMetric.timeInSession) !==
    (assignment?.data.completionMetric ?? CompletionMetric.timeInSession)

  const getFieldsToSave = useCallback(
    async ({ validate }: { validate?: boolean }) => {
      const fieldsToSave: (keyof AssignmentFormValues)[] = (() => {
        switch (stage) {
          case FormStage.Dates:
            return [
              'assignedAt',
              'dueDate',
              'allowLateSubmissions',
              'lateSubmissionCutoff',
            ]
          case FormStage.Grouping:
            return [
              'customGroupingSelected',
              'groupingType',
              'groupingSize',
              'groupingSizeMaximum',
              'forbidStudentGroupCreation',
              'forbidStudentGroupSelection',
              'forbidStudentGroupScheduling',
              'forbidStudentGroupJoining',
              'forbidStudentGroupLeaving',
              'peerReviewsEnabled',
            ]
          case FormStage.PeerReviews:
            return [
              'peerReviewCriteria',
              'peerReviewExcludesSelfReview',
              'peerReviewsHiddenFromReviewees',
            ]
          case FormStage.Grading:
            return [
              'configureGrading',
              'showFinalGradeToParticipants',
              'gradeAppealsEnabled',
            ]
        }
      })()

      if (validate) {
        const isValid = await trigger(fieldsToSave)

        if (!isValid) {
          return false
        }
      }

      // get only the we care about
      const data = getValues(fieldsToSave)
      const toSave = fieldsToSave.reduce<Partial<AssignmentFormValues>>(
        (acc, cur, index) => {
          // @ts-expect-error - we know the types are correct
          acc[cur] = data[index]
          return acc
        },
        {}
      )

      return toSave
    },
    [getValues, stage, trigger]
  )

  const triggerDatesCheck = useCallback(() => {
    trigger(['assignedAt', 'dueDate'])
  }, [trigger])

  const repository = useRepository()

  const cubit = useCubitBuilder(
    () =>
      new CreateOrEditAssignmentCubit({
        repository,
        slideDeckId,
        roles,
      }),
    [repository, slideDeckId, roles]
  )

  const dialogProps: Omit<ComponentProps<typeof Dialog>, 'children'> = {
    size: fullScreen ? 'xxl' : 'xl',
    testId: 'assign-class-dialog',
    className: classNames(
      'flex h-[700px] w-[1000px] items-center justify-center !overflow-hidden',
      {
        'bg-white': fullScreen,
      },
      className
    ),
    innerClassName:
      'flex flex-col h-full w-full items-center gap-4 !min-h-0 overflow-auto',
    'aria-labelledby': headingId,
  }

  // NOTE (mbunsch): We added code that depended on cubit.ready changing,
  // but LtiBaseAssignmentDialog is not an observer
  // however, as making it an observer now might have some side effects that I worry about,
  // instead we just add a reaction here to update local state
  // Down the line, we should refactor this component to be an observer
  const [isReady, setIsReady] = useState(cubit.ready)
  useEffect(() => {
    return reaction(
      () => cubit.ready,
      (ready) => {
        setIsReady(ready)
      }
    )
  }, [cubit])

  if (!isReady) {
    return (
      <Dialog {...dialogProps}>
        <DialogCloseButton />
        <Spinner />
      </Dialog>
    )
  }

  return (
    <Dialog {...dialogProps}>
      <DialogCloseButton />
      <div
        className={classNames('flex flex-col gap-1 text-center', {
          'mb-4': fullScreen,
        })}
      >
        {!forcedStage && (
          <strong className="text-body-large text-on-surface-var">
            {(() => {
              if (currentStageIndex === 0) {
                return tt.instructor_library.step_1()
              } else if (currentStageIndex === 1) {
                return tt.instructor_library.step_2()
              } else if (currentStageIndex === 2) {
                return tt.instructor_library.step_3()
              } else {
                return tt.instructor_library.step_4()
              }
            })()}
          </strong>
        )}
        <h1 id={headingId} className="text-headline-large">
          {forcedStage === FormStage.Dates &&
            tt.instructor_assignment.configure_assignment_dialog.edit_assignment_dates()}
          {forcedStage === FormStage.Grouping &&
            tt.instructor_assignment.configure_assignment_dialog.edit_grouping()}
          {forcedStage === FormStage.PeerReviews &&
            tt.instructor_assignment.configure_assignment_dialog.edit_peer_reviews()}
          {forcedStage === FormStage.Grading &&
            tt.instructor_assignment.configure_assignment_dialog.edit_grading()}
          {!forcedStage &&
            stage === FormStage.Dates &&
            tt.instructor_assignment.configure_assignment_dialog.configure_assignment_dates()}
          {!forcedStage &&
            stage === FormStage.Grouping &&
            tt.instructor_assignment.configure_assignment_dialog.configure_grouping()}
          {!forcedStage &&
            stage === FormStage.PeerReviews &&
            tt.instructor_assignment.configure_assignment_dialog.configure_peer_reviews()}
          {!forcedStage &&
            stage === FormStage.Grading &&
            tt.instructor_assignment.configure_assignment_dialog.configure_grading()}
        </h1>
      </div>

      <form
        className={classNames(
          'flex min-h-0 flex-grow flex-col items-center gap-4',
          {
            'w-[450px]':
              ![FormStage.Grouping, FormStage.PeerReviews].includes(stage) &&
              !wideGrading,
            'w-[880px]': wideGrading,
          }
        )}
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="flex w-full flex-grow flex-col gap-1 px-0.5">
          {sectionSelectComponent && stage === FormStage.Dates
            ? sectionSelectComponent
            : null}

          {ltiMode && errors?.dueDate && (
            <FormError error={errors.dueDate as FieldError} />
          )}
          {ltiMode && errors?.assignedAt && (
            <FormError error={errors.assignedAt as FieldError} />
          )}

          {stage === FormStage.Dates && (
            <>
              <DatesInputs
                control={control}
                setValue={setValue}
                initialAssignedAt={initialAssignedAt}
                initialDueDate={initialDueDate}
                isSubmitting={isSubmitting}
                hasBeenStarted={hasBeenStarted}
                ltiMode={ltiMode}
                isDraft={isDraft}
                assignedAt={assignedAt}
                releasedToStudents={releasedToStudents}
                groupingType={groupingType}
                hideDateFields={hideDateFields}
                dueDate={dueDate}
                formValid={formValid}
                triggerDatesCheck={triggerDatesCheck}
              />
              {!hideDateFields && (
                <LateSubmissionInputs
                  control={control}
                  setValue={setValue}
                  dueDate={dueDate}
                  allowLateSubmissions={allowLateSubmissions}
                  readOnly={hasBeenStarted}
                  cutoffIsAutoDerived={cutoffIsAutoDerived}
                />
              )}
            </>
          )}

          {stage === FormStage.Grouping && (
            <GroupingInputs
              form={form}
              groupingType={groupingType}
              customGroupingSelected={customGroupingSelected}
              forbidStudentGroupCreation={forbidStudentGroupCreation}
              forbidStudentGroupScheduling={forbidStudentGroupScheduling}
              forbidStudentGroupJoining={forbidStudentGroupJoining}
              forbidStudentGroupLeaving={forbidStudentGroupLeaving}
              disableGroupingReason={disableGroupingReason}
              isForcedStage={forcedStage === FormStage.Grouping}
              cubit={cubit}
            />
          )}

          {stage === FormStage.PeerReviews && (
            <PeerReviewInputs form={form} readOnly={false} />
          )}

          {stage === FormStage.Grading && (
            <GradingInputs
              setValue={setValue}
              configureGrading={configureGrading}
              releasedToStudents={releasedToStudents}
              hasQuestions={hasQuestions}
              hasRubrics={hasRubrics}
              scalarsRef={scalarsRef}
              initialAssignmentGradingScalars={initialAssignmentGradingScalars}
              setGradingSumCallback={setGradingSumCallback}
              gradingSumValid={gradingSumValid}
              gradingSum={gradingSum}
              onChange={onGradingChange}
              showFinalGradeToParticipants={
                showFinalGradeToParticipants ?? true
              }
              onShowFinalGradeToParticipantsChange={(show) => {
                setFinalGradeVisibility({
                  setValue,
                  showFinalGradeToParticipants: show,
                })
              }}
              gradeAppealsEnabled={gradeAppealsEnabled ?? false}
              onGradeAppealsEnabledChange={(enabled) => {
                setValue('gradeAppealsEnabled', enabled, {
                  shouldDirty: true,
                })
              }}
              advancedGradingSlot={advancedGradingSlot}
              onCustomizeWeights={onCustomizeWeights}
              completionMetric={completionMetric}
              onCompletionMetricChange={onCompletionMetricChange}
            />
          )}
        </div>

        <div className="flex w-[450px] flex-col justify-end">
          <div
            className={classNames('mt-2 flex w-full flex-row gap-2', {
              'justify-end': forcedStage === undefined,
              'justify-between': forcedStage !== undefined,
            })}
          >
            {showBackButton && previousStage !== null && (
              <BreakoutButton
                className="mr-auto !w-[120px] !min-w-[120px]"
                icon={<ChevronLeft size={15} />}
                kind="secondary"
                size="medium"
                onClick={() => {
                  if (previousStage) {
                    setStage(previousStage)
                  }
                }}
              >
                {tt.instructor_library.back()}
              </BreakoutButton>
            )}

            {(!isDraft || forcedStage) && (
              <BreakoutButton
                size="medium"
                type="button"
                className="!w-[120px] !min-w-[120px] text-nowrap"
                disabled={isLoading}
                kind={earlySecondaryButtonOverride?.kind ?? 'secondary'}
                data-testid="assign-class-secondary-button"
                onClick={
                  earlySecondaryButtonOverride
                    ? async () => {
                        setIsLoadingEarlySecondary(true)
                        await earlySecondaryButtonOverride?.onClick()
                        setIsLoadingEarlySecondary(false)
                      }
                    : popDialog
                }
                loading={isLoadingEarlySecondary}
              >
                {earlySecondaryButtonOverride?.text ??
                  tt.instructor_library.cancel()}
              </BreakoutButton>
            )}

            {forcedStage === undefined && nextStage !== null && (
              <>
                <BreakoutTooltip
                  enabled={isPeerReviewDisabled}
                  content={tt.instructor_assignment.configure_assignment_dialog.peer_review_disabled_max_group_size_one()}
                >
                  <div>
                    <BreakoutButton
                      disabled={isLoadingEarlySecondary || isPeerReviewDisabled}
                      data-testid="assign-class-continue-button"
                      size="medium"
                      type="button"
                      kind="accent"
                      className="!w-[120px] !min-w-[120px]"
                      onClick={async () => {
                        setIsLoading(true)

                        const isValid = await trigger(undefined, {
                          shouldFocus: true,
                        })

                        setIsLoading(false)

                        if (!isValid) return

                        if (onSubmitStage) {
                          const toSave = await getFieldsToSave({
                            validate: false,
                          })

                          if (toSave === false) return

                          // don't await, blocks form progression in UI
                          onSubmitStage(stage, toSave)
                        }

                        if (nextStage) {
                          setStage(nextStage)
                        }
                      }}
                      loading={isLoading}
                    >
                      {tt.instructor_library.continue()}
                    </BreakoutButton>
                  </div>
                </BreakoutTooltip>
              </>
            )}
            {forcedStage === undefined && stage === FormStage.Grading && (
              <BreakoutButton
                size="medium"
                type="button"
                kind="accent"
                className="!w-[120px] !min-w-[120px]"
                disabled={
                  configureGrading === undefined ||
                  (configureGrading && !gradingSumValid) ||
                  (configureGrading &&
                    !!advancedGradingSlot &&
                    !customRubricsValid) ||
                  isLoading
                }
                onClick={async (e) => {
                  e.preventDefault()
                  setIsLoading(true)

                  const values = form.getValues()

                  const result = getLtiAssignmentSchema({ tt }).safeParse(
                    values
                  )
                  if (result.success) {
                    return await onSubmit(result.data).finally(() => {
                      setIsLoading(false)
                    })
                  } else {
                    result.error.errors.forEach((error) => {
                      form.setError(
                        error.path[0] as keyof LtiAssignmentFormValues,
                        {
                          type: 'custom',
                          message: error.message,
                        }
                      )
                    })
                    setIsLoading(false)
                  }
                }}
                data-testid="assign-class-button"
                loading={isSubmitting}
                iconOnRight={true}
                icon={
                  submitButtonIncludeChevron ? (
                    <ChevronRight size={15} />
                  ) : undefined
                }
              >
                {submitButtonText}
              </BreakoutButton>
            )}
            {forcedStage !== undefined && onSubmitStage && (
              <BreakoutTooltip
                enabled={isPeerReviewDisabled}
                content={tt.instructor_assignment.configure_assignment_dialog.peer_review_disabled_max_group_size_one()}
              >
                <div>
                  <BreakoutButton
                    size="medium"
                    type="button"
                    kind="accent"
                    className="!w-[120px] !min-w-[120px]"
                    disabled={
                      //(stage === FormStage.Grading && releasedToStudents) ||
                      (stage === FormStage.Grading &&
                        configureGrading &&
                        !gradingSumValid) ||
                      (stage === FormStage.Grading &&
                        configureGrading &&
                        !!advancedGradingSlot &&
                        !customRubricsValid) ||
                      (stage === FormStage.Grading &&
                        !scalarsDirty &&
                        !completionMetricChanged &&
                        !showFinalGradeChanged &&
                        !gradeAppealsEnabledChanged) ||
                      isLoading ||
                      isPeerReviewDisabled
                    }
                    onClick={async (e) => {
                      e.preventDefault()
                      setIsLoading(true)

                      try {
                        const toSave = await getFieldsToSave({ validate: true })

                        if (toSave === false) {
                          setIsLoading(false)
                          return
                        }

                        await onSubmitStage(stage, toSave)
                        // await forceStageForEdit.onSubmitPartial(toSave)
                      } finally {
                        setIsLoading(false)
                      }
                    }}
                    data-testid="assign-class-button"
                    loading={isSubmitting}
                  >
                    {submitButtonText}
                  </BreakoutButton>
                </div>
              </BreakoutTooltip>
            )}
          </div>
        </div>
      </form>
    </Dialog>
  )
}

const DatesInputs = ({
  control,
  isSubmitting,
  hasBeenStarted,
  ltiMode,
  isDraft,
  initialAssignedAt,
  initialDueDate,
  assignedAt,
  hideDateFields,
  dueDate,
  formValid,
  triggerDatesCheck,
}: {
  control: Control<AssignmentFormValues>
  setValue: UseFormSetValue<AssignmentFormValues>
  isSubmitting: boolean
  hasBeenStarted: boolean
  ltiMode: boolean
  isDraft: boolean
  initialAssignedAt: DateTime | undefined
  initialDueDate: DateTime | undefined
  assignedAt: DateTime
  releasedToStudents: boolean
  groupingType: AssignmentGroupingType
  hideDateFields: boolean
  dueDate: DateTime
  formValid: boolean
  triggerDatesCheck: () => void
}) => {
  const { tt } = useTranslationTyped()

  const showIsAvailableLessThan24Hours = useMemo(() => {
    // if no dueDate no comparison
    if (!dueDate || !assignedAt) return false

    // if dates didn't change, don't show the warning
    if (
      assignedAt === initialAssignedAt &&
      dueDate === initialDueDate &&
      !isDraft
    )
      return

    // A due date that has already passed leaves no window to warn about.
    if (dueDate < DateTime.now()) return false

    // don't compare against now unless assigned at is 5 minutes or more in past
    const nowMinus5Minutes = DateTime.now().minus({ minutes: 5 })

    const availableFrom =
      assignedAt < nowMinus5Minutes ? DateTime.now() : assignedAt

    return dueDate.diff(availableFrom, 'hours').hours < 24
  }, [assignedAt, dueDate, initialAssignedAt, initialDueDate, isDraft])

  // this is a hack as react hook form won't surface the error
  // from the refinement step (dueDate after assignedAt) unless
  // we manually invoke submit or trigger
  useEffect(() => {
    if (!assignedAt || !dueDate) return
    triggerDatesCheck()
  }, [assignedAt, dueDate, triggerDatesCheck])

  return (
    <>
      {!hideDateFields && (
        <>
          <BreakoutTooltip
            enabled={hasBeenStarted || ltiMode}
            content={
              ltiMode
                ? tt.instructor_assignment.disabled_because_lti()
                : tt.instructor_assignment.disabled_because_its_started()
            }
          >
            <div className={ltiMode ? 'hidden' : ''}>
              <Controller
                control={control}
                name="assignedAt"
                render={({ field, fieldState }) => (
                  <BreakoutDateTimeInput
                    {...field}
                    disabled={isSubmitting || hasBeenStarted}
                    error={fieldState.error}
                    type="datetime-local"
                    label={tt.instructor_assignment.available_from()}
                    required={!isDraft}
                    kind="secondary"
                    id="assignment-start-time"
                    hideNowButton
                    onChange={(newAssignedAt) => {
                      field.onChange(newAssignedAt)
                    }}
                    resolution="15min"
                    roundUpToQuarterHour
                  />
                )}
              />
            </div>
          </BreakoutTooltip>
          <BreakoutTooltip
            enabled={ltiMode}
            content={tt.instructor_assignment.disabled_because_lti()}
          >
            <div className={ltiMode ? 'hidden' : ''}>
              <Controller
                control={control}
                name="dueDate"
                render={({ field, fieldState }) => {
                  return (
                    <>
                      <BreakoutDateTimeInput
                        {...field}
                        error={fieldState.error}
                        type="datetime-local"
                        label={tt.instructor_assignment.due_date()}
                        required={!isDraft}
                        kind="secondary"
                        id="meeting-time"
                        hideNowButton
                        min={assignedAt?.plus({ minutes: 15 })}
                        max={assignedAt?.plus({ days: 365 })}
                        initialView={assignedAt?.plus({ days: 1 })}
                        value={field.value}
                        onChange={field.onChange}
                        resolution="15min"
                        roundUpToQuarterHour
                      />
                      {formValid && showIsAvailableLessThan24Hours && (
                        <div className="mt-2 flex w-full flex-row items-center gap-2 rounded-xl bg-core-error px-3 py-2 text-body-medium text-core-on-error">
                          <TriangleAlertIcon size={24} />
                          <span>
                            {tt.instructor_assignment.configure_assignment_dialog.less_than_24_hours_warning()}
                          </span>
                        </div>
                      )}
                    </>
                  )
                }}
              />
            </div>
          </BreakoutTooltip>
        </>
      )}
    </>
  )
}

const GroupingInputs = ({
  form,
  groupingType,
  customGroupingSelected,
  forbidStudentGroupCreation,
  forbidStudentGroupScheduling,
  forbidStudentGroupJoining,
  forbidStudentGroupLeaving,
  disableGroupingReason,
  isForcedStage,
  cubit,
}: {
  form: UseFormReturn<AssignmentFormValues>
  groupingType: AssignmentGroupingType
  customGroupingSelected: boolean | undefined
  forbidStudentGroupCreation: boolean | undefined
  forbidStudentGroupScheduling: boolean | undefined
  forbidStudentGroupJoining: boolean | undefined
  forbidStudentGroupLeaving: boolean | undefined
  disableGroupingReason: string | undefined
  isForcedStage: boolean
  cubit: CreateOrEditAssignmentCubit
}) => {
  const { setValue, watch, trigger, control } = form
  const { tt } = useTranslationTyped()

  const { minGroupSize, maxGroupSize } = useMemo(() => {
    return {
      minGroupSize: cubit.roleGroupSizeConstraints?.minGroupSize ?? 1,
      maxGroupSize:
        cubit.roleGroupSizeConstraints?.maxGroupSize ??
        RoomState.maxAllowedUsers,
    }
  }, [cubit.roleGroupSizeConstraints])

  const [isSimpleStudentConfig, isSimpleInstructorConfig] = useMemo(() => {
    if (groupingType === AssignmentGroupingType.automaticRandom)
      return [false, false]

    // order: [creation, joining, leaving, scheduling]
    const perms = [
      forbidStudentGroupCreation ?? false,
      forbidStudentGroupJoining ?? false,
      forbidStudentGroupLeaving ?? false,
      forbidStudentGroupScheduling ?? false,
    ]
    const studentDefaults = [false, false, false, false]
    const instructorDefaults = [true, true, true, false]

    if (perms.every((perm, i) => perm === studentDefaults[i]))
      return [true, false] as const

    if (perms.every((perm, i) => perm === instructorDefaults[i]))
      return [false, true] as const

    return [false, false] as const
  }, [
    forbidStudentGroupCreation,
    forbidStudentGroupScheduling,
    forbidStudentGroupJoining,
    forbidStudentGroupLeaving,
    groupingType,
  ])

  // simple method doesn't matter if showing custom grouping
  const [showCustomGrouping, simpleGroupingMethod] = useMemo(() => {
    if (customGroupingSelected) return [true, 'instructor'] as const

    if (groupingType === AssignmentGroupingType.automaticRandom)
      return [true, 'instructor'] as const

    if (isSimpleStudentConfig) return [false, 'student'] as const
    if (isSimpleInstructorConfig) return [false, 'instructor'] as const

    // if we dont have a match, show custom view
    return [true, 'instructor'] as const
  }, [
    customGroupingSelected,
    groupingType,
    isSimpleInstructorConfig,
    isSimpleStudentConfig,
  ])

  // if user has somehow entered a state where their config can not be represented by the simple config options
  // but have not toggled it on, we should toggle it on behalf of them
  useEffect(() => {
    if (showCustomGrouping && !customGroupingSelected)
      setValue('customGroupingSelected', true)
  }, [setValue, showCustomGrouping, customGroupingSelected])

  const groupingSize = watch('groupingSize')
  const peerReviewsEnabled = watch('peerReviewsEnabled')
  const isSolo = groupingSize === 1

  // solo groups have no one to join or leave; both mirror creation so the
  // simple student-led / instructor-led presets stay detectable.
  // otherwise: if creation is allowed, joining must also be allowed
  // (creating a group implies joining it)
  useEffect(() => {
    if (isSolo) {
      const forbid = forbidStudentGroupCreation ?? false
      setValue('forbidStudentGroupJoining', forbid)
      setValue('forbidStudentGroupLeaving', forbid)
      return
    }
    if (!forbidStudentGroupCreation)
      setValue('forbidStudentGroupJoining', false)
  }, [isSolo, forbidStudentGroupCreation, setValue])

  useEffect(() => {
    setValue('groupingSizeMaximum', groupingSize) // groupingSizeMaximum is a copy of groupingSize
    trigger(['groupingSize', 'groupingSizeMinimum', 'groupingSizeMaximum'])
  }, [groupingSize, setValue, trigger])

  return (
    <>
      {!showCustomGrouping && (
        <div className="mb-5 flex w-full min-w-[536px] flex-row gap-4 py-1">
          <div
            tabIndex={0}
            className={classNames(
              'flex flex-1 cursor-pointer flex-col gap-2 rounded-[20px] border px-6 py-[26px] outline-offset-[2px]',
              {
                'border-fixed-accent-color': simpleGroupingMethod === 'student',
                'border-outline-light': simpleGroupingMethod !== 'student',
              }
            )}
            onClick={() => {
              setValue('forbidStudentGroupCreation', false)
              setValue('forbidStudentGroupJoining', false)
              setValue('forbidStudentGroupLeaving', false)
              setValue('forbidStudentGroupScheduling', false)
            }}
            onKeyDown={composeClickHandlers(clickOnEnter, clickOnSpace)}
          >
            <PeopleGroup
              size={28}
              className={classNames({
                'stroke-fixed-accent-color': simpleGroupingMethod === 'student',
                'stroke-outline-light': simpleGroupingMethod !== 'student',
              })}
            />
            <div className="flex flex-col">
              <div className="flex flex-row items-center gap-1">
                <strong className="text-title-large">
                  {tt.instructor_assignment.groups.student_led_grouping()}
                </strong>
                <BreakoutInfoTooltip
                  placement="bottom"
                  content={
                    <TooltipWrapper>
                      <ul className="list-disc pl-3">
                        <Trans
                          i18nKey="instructor_assignment.configure_assignment_dialog.student_led_grouping_tooltip_list_items"
                          components={{
                            li: <li />,
                          }}
                        />
                      </ul>
                    </TooltipWrapper>
                  }
                />
              </div>
              <strong className="text-body-medium text-on-surface-var">
                {tt.instructor_assignment.configure_assignment_dialog.student_led_grouping_description()}
              </strong>
            </div>
          </div>
          <div
            tabIndex={0}
            className={classNames(
              'flex flex-1 cursor-pointer flex-col gap-2 rounded-[20px] border px-6 py-[26px] outline-offset-[2px]',
              {
                'border-fixed-accent-color':
                  simpleGroupingMethod === 'instructor',
                'border-outline-light': simpleGroupingMethod !== 'instructor',
              }
            )}
            onClick={() => {
              setValue('forbidStudentGroupCreation', true)
              setValue('forbidStudentGroupJoining', true)
              setValue('forbidStudentGroupLeaving', true)
              setValue('forbidStudentGroupScheduling', false)
            }}
            onKeyDown={composeClickHandlers(clickOnEnter, clickOnSpace)}
          >
            <Person
              size={28}
              className={classNames({
                'stroke-fixed-accent-color':
                  simpleGroupingMethod === 'instructor',
                'stroke-outline-light': simpleGroupingMethod !== 'instructor',
              })}
            />
            <div className="flex flex-col">
              <div className="flex flex-row items-center gap-1">
                <strong className="text-title-large">
                  {tt.instructor_assignment.groups.instructor_led_grouping()}
                </strong>
                <BreakoutInfoTooltip
                  placement="bottom"
                  content={
                    <TooltipWrapper>
                      <ul className="list-disc pl-3">
                        <Trans
                          i18nKey="instructor_assignment.configure_assignment_dialog.instructor_led_grouping_tooltip_list_items"
                          components={{
                            li: <li />,
                          }}
                        />
                      </ul>
                    </TooltipWrapper>
                  }
                />
              </div>
              <strong className="text-body-medium text-on-surface-var">
                {tt.instructor_assignment.configure_assignment_dialog.instructor_led_grouping_description()}
              </strong>
            </div>
          </div>
        </div>
      )}
      {showCustomGrouping && (
        <div className="mb-5 flex w-full min-w-[400px] flex-col items-center gap-3 rounded-2xl border border-outline-light p-5 text-body-medium shadow-sm">
          <strong className="text-on-surface-var">
            {tt.instructor_assignment.configure_assignment_dialog.customize_group_settings()}
          </strong>
          <div className="h-[1px] w-full bg-outline-light" />
          {(
            [
              'forbidStudentGroupCreation',
              'forbidStudentGroupJoining',
              'forbidStudentGroupLeaving',
              'forbidStudentGroupScheduling',
            ] as const
          ).map((k) => {
            // if creation is allowed, joining must also be allowed (creating a group implies joining it)
            const disableJoining =
              k === 'forbidStudentGroupJoining' &&
              forbidStudentGroupCreation === false

            // solo groups have no one to join or leave
            const soloForced =
              isSolo &&
              (k === 'forbidStudentGroupJoining' ||
                k === 'forbidStudentGroupLeaving')

            const disabled =
              soloForced || disableJoining || !!disableGroupingReason
            const disabledReason = soloForced
              ? tt.instructor_assignment.configure_assignment_dialog.solo_group_switch_tooltip()
              : (disableGroupingReason ??
                tt.instructor_assignment.groups.disabled_forbid_group_selection_tooltip())

            const label: string = (() => {
              switch (k) {
                case 'forbidStudentGroupJoining': {
                  return tt.instructor_assignment.configure_assignment_dialog.allow_join()
                }
                case 'forbidStudentGroupLeaving': {
                  return tt.instructor_assignment.configure_assignment_dialog.allow_leave()
                }
                case 'forbidStudentGroupCreation': {
                  return tt.instructor_assignment.configure_assignment_dialog.allow_create()
                }
                case 'forbidStudentGroupScheduling': {
                  return tt.instructor_assignment.configure_assignment_dialog.allow_group_meeting_time()
                }
              }
            })()
            return (
              <Controller
                key={k + '_switch'}
                control={control}
                name={k}
                render={({ field }) => {
                  return (
                    <BreakoutTooltip
                      enabled={disabled}
                      placement="right"
                      className="whitespace-pre-wrap"
                      content={disabledReason}
                    >
                      <div className="flex w-full flex-row items-center justify-between">
                        <strong
                          className={classNames('text-body-medium', {
                            'text-on-surface-disabled': disabled,
                          })}
                        >
                          {label}
                        </strong>
                        <BreakoutSwitch
                          initialValue={
                            field.value !== undefined ? !field.value : true
                          }
                          disabled={disabled}
                          accent
                          onChange={(e) => {
                            const allow = e.target.checked
                            field.onChange(!allow)
                          }}
                        />
                      </div>
                    </BreakoutTooltip>
                  )
                }}
              />
            )
          })}

          <Controller
            control={control}
            disabled={!!disableGroupingReason}
            name="groupingType"
            render={({ field }) => (
              <BreakoutTooltip
                enabled={!!disableGroupingReason}
                placement="right"
                className="whitespace-pre-wrap"
                content={disableGroupingReason ?? ''}
              >
                <div className="flex w-full flex-row items-center justify-between">
                  <strong
                    className={classNames('text-body-medium', {
                      'text-on-surface-disabled': !!disableGroupingReason,
                    })}
                  >
                    {tt.instructor_assignment.configure_assignment_dialog.auto_assign_students()}
                  </strong>

                  <BreakoutSwitch
                    initialValue={
                      field.value === AssignmentGroupingType.automaticRandom
                    }
                    disabled={!!disableGroupingReason}
                    accent
                    onChange={(e) => {
                      const groupingType = e.target.checked
                        ? AssignmentGroupingType.automaticRandom
                        : AssignmentGroupingType.manual

                      field.onChange(groupingType)
                    }}
                  />
                </div>
              </BreakoutTooltip>
            )}
          />
        </div>
      )}
      <strong
        className={classNames('mx-auto mb-5 text-body-medium underline', {
          'cursor-not-allowed text-on-surface-disabled':
            !!disableGroupingReason,
          'cursor-pointer text-on-surface': !disableGroupingReason,
        })}
        tabIndex={0}
        onClick={() => {
          if (disableGroupingReason) return
          const isSimpleConfig =
            isSimpleStudentConfig || isSimpleInstructorConfig

          // if moving back to simple select and not simple config, set fields to default
          if (showCustomGrouping) {
            if (!isSimpleConfig) {
              setValue('forbidStudentGroupScheduling', false)
              setValue('forbidStudentGroupCreation', false)
              setValue('forbidStudentGroupJoining', false)
              setValue('forbidStudentGroupLeaving', false)
              setValue('groupingType', AssignmentGroupingType.manual)
            }
            return setValue('customGroupingSelected', false)
          }

          // if moving to advanced, leave all fields as is
          return setValue('customGroupingSelected', true)
        }}
        onKeyDown={composeClickHandlers(clickOnEnter, clickOnSpace)}
      >
        {showCustomGrouping
          ? tt.instructor_assignment.configure_assignment_dialog.return_to_simple_group_settings()
          : tt.instructor_assignment.configure_assignment_dialog.advanced_group_settings()}
      </strong>
      <div className="mx-auto w-full max-w-[450px]">
        <AdminOnlyRing label="" ringClassName="mt-2" onlyShowIf={false}>
          <Controller
            control={control}
            name="assignmentType"
            render={({ field, fieldState }) => (
              <BreakoutSelect
                {...field}
                error={fieldState.error}
                required
                kind="secondary"
                label={tt.instructor_library.assignment_type()}
                name="assignmentType"
                options={[
                  {
                    value: AssignmentType.ManyGroups,
                    label: tt.instructor_library.assignment_type_student_led(),
                  },
                  {
                    value: AssignmentType.OneGroup,
                    label:
                      tt.instructor_library.assignment_type_instructor_led(),
                  },
                ]}
              />
            )}
          />
        </AdminOnlyRing>

        <GroupingSizeInput
          form={form}
          minGroupSize={minGroupSize}
          maxGroupSize={maxGroupSize}
          disableGroupingReason={disableGroupingReason}
          readOnly={false}
          roleGroupSizeConstraints={cubit.roleGroupSizeConstraints}
          roles={cubit.roles.models}
        />

        {!isForcedStage && (
          <PeerReviewsToggle
            control={control}
            setValue={setValue}
            groupingSize={groupingSize}
            peerReviewsEnabled={peerReviewsEnabled}
            isForcedStage={isForcedStage}
          />
        )}
      </div>
    </>
  )
}

const GradingInputs = ({
  setValue,
  configureGrading,
  releasedToStudents,
  hasQuestions,
  hasRubrics,
  scalarsRef,
  initialAssignmentGradingScalars,
  setGradingSumCallback,
  gradingSumValid,
  gradingSum,
  onChange,
  showFinalGradeToParticipants,
  onShowFinalGradeToParticipantsChange,
  gradeAppealsEnabled,
  onGradeAppealsEnabledChange,
  advancedGradingSlot,
  onCustomizeWeights,
  completionMetric,
  onCompletionMetricChange,
}: {
  setValue: UseFormSetValue<AssignmentFormValues>
  configureGrading: boolean | undefined
  releasedToStudents: boolean
  hasQuestions: boolean
  hasRubrics: boolean
  scalarsRef: React.RefObject<
    SectionAssignment['data']['assignmentGradingScalars']
  >
  initialAssignmentGradingScalars: SectionAssignment['data']['assignmentGradingScalars']
  setGradingSumCallback: (gradingSum: {
    isValid: boolean
    sumAsInt: number
  }) => void
  gradingSumValid: boolean
  gradingSum: number
  currentScalarsToCheckDirty?:
    | SectionAssignment['data']['assignmentGradingScalars']
    | null
  onChange?: ({
    scalars,
    configureGrading,
  }: {
    scalars: SectionAssignment['data']['assignmentGradingScalars']
    configureGrading: boolean
  }) => void
  showFinalGradeToParticipants: boolean
  onShowFinalGradeToParticipantsChange: (checked: boolean) => void
  gradeAppealsEnabled: boolean
  onGradeAppealsEnabledChange: (checked: boolean) => void
  advancedGradingSlot?: ReactNode
  onCustomizeWeights?: () => void
  completionMetric?: CompletionMetric
  onCompletionMetricChange?: (metric: CompletionMetric) => void
}) => {
  const { tt } = useTranslationTyped()
  const labelId = useId()
  const finalScoreTooltip =
    tt.instructor_library.display_final_score_to_participants_tooltip_lms()

  // Memoize the onChange handler to prevent unnecessary re-renders
  const handleGradingScalarsChange = useCallback(
    (v: {
      participationScore: number
      quizScore: number
      totalRubricScore: number
      attendanceScore: number
      isValid: boolean
      sumAsInt: number
    }) => {
      setGradingSumCallback(v)
      // Extract just the scalar values to pass to the onChange callback
      const {
        participationScore,
        quizScore,
        totalRubricScore,
        attendanceScore,
      } = v
      onChange &&
        onChange({
          scalars: {
            participationScore,
            quizScore,
            totalRubricScore,
            attendanceScore,
          },
          configureGrading: true,
        })
    },
    [setGradingSumCallback, onChange]
  )

  const handleConfigureGradingTrue = useCallback(() => {
    setValue('configureGrading', true)
    onChange &&
      onChange({
        scalars: scalarsRef.current ?? undefined,
        configureGrading: true,
      })
  }, [setValue, onChange, scalarsRef])

  const handleConfigureGradingFalse = useCallback(() => {
    setValue('configureGrading', false)
    onChange &&
      onChange({
        scalars: scalarsRef.current ?? undefined,
        configureGrading: false,
      })
  }, [setValue, onChange, scalarsRef])

  const radioGroupValue = (() => {
    if (configureGrading === undefined) {
      return null
    }
    if (configureGrading) {
      return 'yes'
    } else {
      return 'no'
    }
  })()

  return (
    <>
      {releasedToStudents && (
        <div className="mb-3 flex flex-row items-center justify-center gap-2 rounded-xl border border-outline-light px-3 py-2">
          <TriangleAlertIcon size={24} className="stroke-core-error" />
          <div className="text-body-medium">
            {tt.instructor_library.published_assignment_grading_warning()}
          </div>
        </div>
      )}
      <h3 className="mb-1 text-title-medium" id={labelId}>
        {tt.instructor_library.grade_your_students()}
      </h3>
      <>
        <BreakoutRadioGroup
          aria-labelledby={labelId}
          value={radioGroupValue}
          onValueChange={(val) => {
            if (val === 'yes') handleConfigureGradingTrue()
            else if (val === 'no') handleConfigureGradingFalse()
          }}
          className="flex w-full flex-row gap-1"
        >
          <BreakoutRadioButton
            data-testid="configure-grading-true"
            label={tt.instructor_library.yes()}
            value="yes"
          />
          <BreakoutRadioButton
            data-testid="configure-grading-false"
            label={tt.instructor_library.no()}
            value="no"
          />
        </BreakoutRadioGroup>
        <FormError
          error={
            configureGrading === undefined
              ? {
                  message: tt.instructor_library.grading_required(),
                  type: 'custom',
                }
              : undefined
          }
        />
      </>
      <div className="mt-3 flex w-full">
        {configureGrading === false && (
          <p className="text-body-large text-on-surface-var">
            {tt.instructor_library.no_grading_description()}
          </p>
        )}
        {configureGrading && (
          <div className="flex w-full flex-row gap-8">
            <div className="flex flex-[3] flex-col gap-3">
              <h3 className="border-b border-outline-light pb-2 text-title-medium">
                {tt.instructor_library.configure_scalars_title()}
              </h3>
              <ConfigureGradingScalars
                //disableAll={releasedToStudents}
                hasQuizQuestions={hasQuestions}
                hasRubrics={hasRubrics}
                ref={scalarsRef}
                defaults={initialAssignmentGradingScalars}
                onChange={handleGradingScalarsChange}
                completionMetric={completionMetric}
                onCompletionMetricChange={onCompletionMetricChange}
              />
              {advancedGradingSlot && onCustomizeWeights && (
                <button
                  type="button"
                  data-testid="customize-weights-link"
                  onClick={onCustomizeWeights}
                  className="self-start text-left text-label-medium text-on-surface-var underline"
                >
                  {tt.instructor_library.custom_rubrics_advanced_title()}
                </button>
              )}
              <div className="flex flex-col gap-1 border-t border-outline-light pt-2">
                <div className="flex flex-row justify-between">
                  <strong className="text-title-medium">
                    {tt.instructor_library.total()}
                  </strong>
                  <div
                    className={classNames(
                      'flex flex-row items-center gap-1 text-title-medium',
                      {
                        'text-core-error': !gradingSumValid,
                      }
                    )}
                  >
                    {!gradingSumValid && <TriangleAlertIcon size={12.5} />}
                    <strong>{gradingSum + '%'}</strong>
                  </div>
                </div>
                <div className="flex flex-row items-center gap-1 text-label-medium text-on-surface-var">
                  <InfoIcon size={15} />
                  <span>
                    {tt.instructor_library.select_grading_weights_description()}
                  </span>
                </div>
              </div>
              <div className="pr-1.5 pt-4.5">
                <BreakoutSwitch
                  checked={showFinalGradeToParticipants ?? true}
                  disabled={releasedToStudents}
                  onChange={(e) =>
                    onShowFinalGradeToParticipantsChange?.(e.target.checked)
                  }
                  className="w-full"
                  accent={true}
                  labelPosition="left"
                  labelClassName="!pl-0 w-full"
                  label={
                    <div className="flex flex-row items-center gap-1 text-label-large text-on-surface">
                      <label>
                        {tt.instructor_library.display_final_score_to_participants()}
                      </label>
                      <BreakoutInfoTooltip content={finalScoreTooltip} />
                    </div>
                  }
                  testId="show-final-grade-to-participants"
                  tooltip={finalScoreTooltip}
                />
                <div className="pt-3">
                  <GradeAppealsSwitch
                    checked={gradeAppealsEnabled}
                    disabled={releasedToStudents}
                    showFinalGradeToParticipants={showFinalGradeToParticipants}
                    onChange={onGradeAppealsEnabledChange}
                  />
                </div>
              </div>
            </div>
            {advancedGradingSlot && (
              <div className="flex flex-[2] flex-col border-l border-outline-light pl-8">
                {advancedGradingSlot}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function checkScalarsAreEqual(
  a: SectionAssignment['data']['assignmentGradingScalars'] | null,
  b: SectionAssignment['data']['assignmentGradingScalars'] | null
) {
  // if scalars are both falsey or exactly equal
  if (a === b || (!a && !b)) return true

  // if one is falsey but not the other
  if (!a || !b) return false

  // compare individual fields
  return (
    a.attendanceScore === b.attendanceScore &&
    a.participationScore === b.participationScore &&
    a.quizScore === b.quizScore &&
    a.totalRubricScore === b.totalRubricScore
  )
}
